import React, { useEffect, useRef, useState } from "react";
import TopBar from "@/components/TopBar";
import { apiUpload } from "@/lib/api";
import {
  ScanLine, Upload, Save, Loader2, Camera, FileText, Plus, X, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Scanner v4 — PDF-first.
 *
 *   Every scan produces exactly one PDF, regardless of how many pages
 *   the user captured (single-image scans are converted to a 1-page PDF).
 *   No OCR is performed here — documents are saved as-is per user request.
 *   Categorisation still happens (backend runs a lightweight vision classifier).
 *
 *   Flow:
 *     1) User taps Camera / Upload / +Add page
 *     2) Preview thumbnails appear; user can remove any
 *     3) User names it (single field) → Save
 *     4) Everything combines into ONE PDF via /api/scans/pdf
 *        (ready-made PDFs picked from device upload as separate documents)
 *     5) Upon success we auto-navigate to /app/documents so the user
 *        sees the new item in place — no extra tap.
 */
export default function Scanner() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const [pages, setPages] = useState([]); // [{file, previewUrl, isPdf}]
  const [name, setName] = useState("");
  const [category, setCategory] = useState("scan");
  const [processing, setProcessing] = useState(false);

  // Deep-link: /app/scanner?title=Aadhaar&category=aadhaar
  useEffect(() => {
    const t = searchParams.get("title");
    const c = searchParams.get("category");
    if (t) setName(t);
    if (c) setCategory(c);
  }, [searchParams]);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    const additions = picked.map((f) => ({
      file: f,
      previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      isPdf: f.type === "application/pdf",
    }));
    setPages((p) => [...p, ...additions]);
  };

  const removePage = (idx) => {
    setPages((p) => {
      const target = p[idx];
      if (target?.previewUrl) { try { URL.revokeObjectURL(target.previewUrl); } catch { /* noop */ } }
      return p.filter((_, i) => i !== idx);
    });
  };

  const clearAll = () => {
    pages.forEach((p) => { try { p.previewUrl && URL.revokeObjectURL(p.previewUrl); } catch { /* noop */ } });
    setPages([]);
  };

  const save = async () => {
    if (!pages.length) return;
    const finalName = (name || "").trim() || "Scanned document";
    setProcessing(true);
    try {
      const imageParts = pages.filter((p) => !p.isPdf).map((p) => p.file);
      const pdfParts = pages.filter((p) => p.isPdf).map((p) => p.file);

      // Combine every captured image into one PDF (even a single image → 1-page PDF).
      if (imageParts.length) {
        const fd = new FormData();
        imageParts.forEach((f) => fd.append("files", f));
        fd.append("title", pdfParts.length ? `${finalName} (scans)` : finalName);
        fd.append("category", category || "scan");
        await apiUpload("/scans/pdf", fd);
      }

      // Any device PDFs are stored as their own documents (kept as-is).
      for (const pdf of pdfParts) {
        const fd = new FormData();
        fd.append("file", pdf);
        fd.append("title", finalName);
        fd.append("category", category || "scan");
        // ocr intentionally omitted — backend default is off.
        await apiUpload("/documents", fd);
      }

      toast.success("Saved as PDF — opening documents");
      clearAll();
      setName("");
      // Auto-navigate so the newly-created doc reflects immediately.
      setTimeout(() => nav("/app/documents"), 350);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <TopBar title="Scanner" subtitle="Snap or upload — saved as PDF" back />

      <div className="px-4 py-4 space-y-4">
        {pages.length === 0 && (
          <div className="rounded-3xl border border-border bg-card p-6 text-center">
            <div className="h-14 w-14 mx-auto rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
              <ScanLine className="h-6 w-6" />
            </div>
            <div className="font-display font-semibold text-lg mt-3">Scan a document</div>
            <div className="text-sm text-muted-foreground mt-1">
              Capture with camera or pick a file. Every scan is saved as one PDF — single page or multi-page.
            </div>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid="scanner-camera-input" />
            <input ref={fileRef} type="file" accept="*/*" multiple className="hidden" onChange={onPick} data-testid="scanner-file-input" />
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button onClick={() => cameraRef.current?.click()} className="h-11" data-testid="scanner-capture-button">
                <Camera className="h-4 w-4 mr-1.5" /> Camera
              </Button>
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-11" data-testid="scanner-upload-button">
                <Upload className="h-4 w-4 mr-1.5" /> Upload
              </Button>
            </div>
          </div>
        )}

        {pages.length > 0 && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2" data-testid="scanner-pages-grid">
              {pages.map((p, idx) => (
                <div key={idx} className="relative rounded-xl overflow-hidden border border-border bg-card aspect-[3/4]" data-testid="scanner-page-thumb">
                  {p.previewUrl ? (
                    <img src={p.previewUrl} alt={`page ${idx + 1}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full p-3 flex flex-col items-center justify-center text-center">
                      <FileText className="h-6 w-6 text-muted-foreground" />
                      <div className="text-[10px] mt-1 truncate w-full">{p.file.name}</div>
                      {p.isPdf && <Badge className="rounded-full text-[9px] mt-1">PDF</Badge>}
                    </div>
                  )}
                  <div className="absolute top-1 left-1 h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-semibold">
                    {idx + 1}
                  </div>
                  <button
                    type="button"
                    onClick={() => removePage(idx)}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/90 border border-border flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground"
                    aria-label={`Remove ${idx + 1}`}
                    data-testid={`scanner-remove-page-${idx}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                className="aspect-[3/4] rounded-xl border-2 border-dashed border-border bg-card flex flex-col items-center justify-center gap-1 text-muted-foreground hover:bg-[hsl(var(--accent))]"
                data-testid="scanner-add-page-button"
              >
                <Plus className="h-6 w-6" />
                <span className="text-[11px] font-medium">Add page</span>
              </button>
            </div>

            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
            <input ref={fileRef} type="file" accept="*/*" multiple className="hidden" onChange={onPick} />

            <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-10 w-full" data-testid="scanner-upload-more">
              <Upload className="h-4 w-4 mr-1.5" /> Add another file
            </Button>

            <div>
              <Label className="text-xs">Save as (name)</Label>
              <Input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Aadhaar, PAN Card, Lease Agreement"
                data-testid="scanner-name-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                {pages.length === 1 ? "1 page → 1-page PDF." : `Combining ${pages.length} pages into one PDF.`}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" onClick={clearAll} className="h-11 col-span-1" data-testid="scanner-clear-button" disabled={processing}>
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
              <Button
                onClick={save}
                disabled={processing || !pages.length}
                className="h-11 col-span-2"
                data-testid="scanner-process-button"
              >
                {processing ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Saving…</>
                ) : (
                  <><Save className="h-4 w-4 mr-1.5" /> Save PDF</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
